import { CircleAlertIcon, RefreshCcw } from "lucide-react";
import { useMemo } from "react";
import { useIntl } from "react-intl";
import Markdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import remarkBreaks from "remark-breaks";
import remarkGfm from "remark-gfm";
import type { Language, ReportSection } from "~/api/model";
import { Alert, AlertDescription } from "~/components/ui/alert";
import { Button } from "~/components/ui/button";
import { Skeleton } from "~/components/ui/skeleton";
import {
  TERMS_PROSE_CLASS,
  getTermsMarkdownComponents,
  highlightTermsInMarkdown,
  unwrapAllCodeBlocks,
} from "~/utils/markdownTerms";
import { Hint } from "../../types";
import { ReportSectionView } from "./ReportSection";
import { OutdatedReason } from "./TemplateReportSelector";

export type ReportViewStatus =
  | { type: "loading" }
  | { type: "pending" }
  | { type: "ready" }
  | { type: "outdated"; reason?: OutdatedReason }
  | { type: "templateDeleted" };

type ReportViewProps = {
  reportSections: ReportSection[];
  status: ReportViewStatus;
  terms: Hint[];
  content: string;
  onRegenerateSection: (sectionId: string) => void;
  onEditSection: (sectionId: string, content: string) => Promise<void>;
  onRegenerateReport?: () => void;
  isRegeneratingReport?: boolean;
  templateName?: string | null;
  outputLanguage?: Language | null;
  isActionsAvailable?: boolean;
};

export const ReportView = ({
  reportSections,
  status,
  terms,
  content,
  onRegenerateSection,
  onEditSection,
  onRegenerateReport,
  isRegeneratingReport,
  templateName,
  outputLanguage,
  isActionsAvailable = true,
}: ReportViewProps) => {
  const { formatMessage } = useIntl();
  const highlightedContent = useMemo(() => {
    if (!content) return "";
    const preprocessed = unwrapAllCodeBlocks(content);
    return highlightTermsInMarkdown(preprocessed, terms);
  }, [content, terms]);

  if (status.type === "loading") {
    return (
      <div className="max-w-sm animate-pulse">
        {Array.from({ length: 3 }).map((_, index) => (
          <div key={index}>
            {index % 2 === 0 ? (
              <Skeleton className="h-4 bg-gray-200 rounded-full dark:bg-gray-700 w-48 mb-4" />
            ) : (
              <Skeleton className="h-4 bg-gray-200 rounded-full dark:bg-gray-700 w-32 mb-4" />
            )}
            <Skeleton className="h-3 bg-gray-200 rounded-full dark:bg-gray-700 max-w-[360px] mb-2.5" />
            <Skeleton className="h-3 bg-gray-200 rounded-full dark:bg-gray-700 mb-2.5" />
            <Skeleton className="h-3 bg-gray-200 rounded-full dark:bg-gray-700 max-w-[330px] mb-2.5" />
            <Skeleton className="h-3 bg-gray-200 rounded-full dark:bg-gray-700 max-w-[300px] mb-2.5" />
            <Skeleton className="h-3 bg-gray-200 rounded-full dark:bg-gray-700 max-w-[360px] mb-6" />
          </div>
        ))}
      </div>
    );
  }

  if (reportSections.length > 0) {
    const getOutdatedMessage = () => {
      if (status.type === "outdated") {
        if (status.reason === "outdatedByDoclist") {
          return formatMessage({ id: "chat.report.outdatedByDoclistAlert" });
        }

        if (status.reason === "outdatedByTemplate") {
          return formatMessage({ id: "chat.report.outdatedByTemplateAlert" });
        }

        return formatMessage({ id: "chat.report.outdatedAlert" });
      }
      return "";
    };

    return (
      <div className="flex flex-col gap-4">
        {isActionsAvailable && status.type === "outdated" && (
          <Alert
            variant="default"
            className="border-amber-200 bg-amber-50 px-3 py-1.5"
          >
            <div className="flex items-center gap-2">
              <AlertDescription className="flex-1 text-amber-800 text-xs">
                {getOutdatedMessage()}
              </AlertDescription>
              {onRegenerateReport && (
                <Button
                  variant="ghost"
                  size="mini"
                  className="shrink-0 text-amber-700 hover:bg-amber-100 hover:text-amber-900 gap-1"
                  onClick={onRegenerateReport}
                  disabled={isRegeneratingReport}
                >
                  <RefreshCcw className="h-3 w-3" />
                  {formatMessage({ id: "chat.report.rewriteNow" })}
                </Button>
              )}
            </div>
          </Alert>
        )}
        {isActionsAvailable && status.type === "templateDeleted" && (
          <Alert variant="default" className="border-gray-200 bg-gray-50">
            <AlertDescription className="flex items-center gap-2 text-gray-800">
              <CircleAlertIcon className="h-4 w-4" />
              {formatMessage({ id: "chat.report.templateDeletedAlert" })}
            </AlertDescription>
          </Alert>
        )}
        {reportSections
          .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
          .map((section) => (
            <ReportSectionView
              key={section.id}
              reportSection={section}
              terms={terms}
              onRegenerate={() => onRegenerateSection(section.id ?? "")}
              onEdit={async (content: string) =>
                onEditSection(section.id ?? "", content)
              }
              isRegenerating={
                section.status === "processing" || section.status === "pending"
              }
              status={status}
              templateName={templateName}
              outputLanguage={outputLanguage}
              isActionAvailable={isActionsAvailable}
            />
          ))}
      </div>
    );
  }

  return (
    <Markdown
      remarkPlugins={[remarkGfm, remarkBreaks]}
      rehypePlugins={[rehypeRaw]}
      className={TERMS_PROSE_CLASS}
      components={{
        ...getTermsMarkdownComponents(terms),
        h1: (props) => (
          <h1 {...props} className="text-lg font-medium mb-2 normal-case" />
        ),
        h2: (props) => <h2 {...props} className="text-base font-bold mb-1" />,
        h3: (props) => (
          <h3 {...props} className="text-sm font-medium uppercase mb-1" />
        ),
      }}
    >
      {highlightedContent}
    </Markdown>
  );
};
